"use client"

// [VV13-TEARDOWN] lp-scrollytelling-v2 A/B test: delete this file at teardown.
// See the checklist in @local/tracking/src/experiments/scrollytelling.
import { useEffect } from "react"
import { usePathname } from "next/navigation"
import Cookies from "js-cookie"
import { trackExperimentExposure } from "@local/tracking/src/experiments/exposure"
import {
  getVv13EnvIncompatibility,
  isVv13ExposurePath,
  isVv13Variant,
  VV13_BAILOUT_GROUP,
  VV13_COOKIE,
  VV13_FEATURE_FLAG,
  type Vv13ExposureGroup,
} from "@local/tracking/src/experiments/scrollytelling"

/**
 * Signals lp-scrollytelling-v2 exposure. Middleware buckets server-side on the
 * rain-jacket LP home; without this call the experiment shows zero enrolled
 * users (PostHog only counts a person as enrolled on a `$feature_flag_called`
 * event).
 *
 * Mounted on BOTH arms' landing pages (the V6 LP for `control` and the
 * scrollytelling page for `test`), because the brief's primary metric is
 * %LP → Checkout, so enrolment has to happen at the top of the funnel being
 * measured. Firing on the checkout instead would put enrolment downstream of
 * the very step under test and force the metric toward 100%, which is what
 * happened to vv10 (its checkout rate read ~98%).
 *
 * THIS IS AN A/B/C TEST WHERE C IS THE DISCARD GROUP. A visitor who fails the
 * deterministic compatibility probe is exposed as the third variant `bailout`
 * instead of an arm, on EITHER arm's page. Arm-enrolling them would pollute
 * `test` with mechanical exits and control-page conversions; silently
 * dropping them would hide how many people bail and why. The C exposure
 * carries `vv13_bailout_reason` so the why is visible in PostHog. Compare A
 * vs B only; C is observation, and because the divert is symmetric the A/B
 * SRM stays valid. (An actual bounce demotes the visitor's cookie to
 * `control` (see `useExperienceCompatibility`), but their environment still
 * fails the probe here, so they keep surfacing as C, not as control.)
 *
 * Reads the COOKIE rather than a server-resolved prop, because the arm has to
 * be readable on both surfaces without opting either route into dynamic
 * rendering. The cookie only exists for visitors the middleware bucketed on
 * the two landing pages, so its presence is the "in experiment" signal.
 *
 * Self-gating on pathname and cookie, so mounting it on a page it doesn't
 * apply to is a no-op.
 */
export const Vv13ExposureBeacon = () => {
  const pathname = usePathname()

  useEffect(() => {
    const vv13 = Cookies.get(VV13_COOKIE)
    if (!isVv13Variant(vv13)) {
      return
    }
    if (!isVv13ExposurePath(pathname)) {
      return
    }

    // The C-group divert, resolved BEFORE the QA-override skip below: the
    // accessibility bounce lands with `?vv13=control` (the demote), and those
    // visitors must still be counted in C, not silently dropped as QA.
    const bailoutReason = getVv13EnvIncompatibility()
    const group: Vv13ExposureGroup = bailoutReason ? VV13_BAILOUT_GROUP : vv13

    // Don't ARM-enrol QA sessions driven by a VALID `?vv13=` override: they'd
    // bias the read. Checked on production too, unlike vv5: vv5 gates its
    // override to non-prd in middleware, so a prd visitor carrying `?vv5=` is
    // a genuine cookie-bucketed participant. vv13 honours the override
    // everywhere (it only swaps which landing page renders, so there's
    // nothing to game). A bogus value (`?vv13=xyz`) is ignored by middleware
    // and falls through to a real bucket, so those visitors MUST still be
    // counted. C-group exposures fire regardless: an incompatible
    // environment is a fact about the visitor, not the QA session.
    if (
      !bailoutReason &&
      typeof window !== "undefined" &&
      isVv13Variant(
        new URLSearchParams(window.location.search).get(VV13_COOKIE)
      )
    ) {
      return
    }

    // Dedupe across remounts (keyed by group, so a test → bailout demote still
    // fires), the register-before-capture ordering, the no-posthog-ready-gate
    // decision and the Bloomreach/GA/datalake legs all live in the shared
    // helper. The bailout reason goes on both legs, under each leg's own key.
    trackExperimentExposure({
      flag: VV13_FEATURE_FLAG,
      variant: group,
      postHogProperties: bailoutReason
        ? { vv13_bailout_reason: bailoutReason }
        : undefined,
      eventProperties: bailoutReason
        ? { bailout_reason: bailoutReason }
        : undefined,
    })
  }, [pathname])

  return null
}
